import { flattenBlockCatalog } from '@/features/forms/adapters/formsAdapters';
import { buildCatalogIndex } from '@/features/forms/adapters/formsBlockConfig';
import { formsApi } from '@/features/forms/api/formsApi';
import type {
  FormBlock,
  FormBlockCatalogEntry,
  FormBlockConfig,
  ResolvedForm,
  ResolvedFormBlock,
} from '@/features/forms/api/types';

function lookupCatalogForBlock(
  index: Map<string, FormBlockCatalogEntry>,
  block: FormBlock,
): FormBlockCatalogEntry | undefined {
  const blockType = block.block_type ?? block.type ?? '';
  const candidates = [blockType, blockType.replace(/^sb_/, ''), blockType.replace(/_/g, '-')];
  for (const candidate of candidates) {
    const match = index.get(candidate.toLowerCase());
    if (match) return match;
  }
  return undefined;
}

function readBlockPosition(block: FormBlock): number {
  return block.position ?? block.order ?? 0;
}

function adaptCatalogFields(
  catalogEntry: FormBlockCatalogEntry | undefined,
  config: FormBlockConfig | undefined,
): ResolvedFormBlock['catalog'] {
  const rawFields = catalogEntry?.fields;
  if (!Array.isArray(rawFields) || rawFields.length === 0) return undefined;

  const overrides =
    (config?.field_overrides as Record<string, Record<string, unknown>> | undefined) ??
    ({} as Record<string, Record<string, unknown>>);

  const fields = rawFields
    .map((field) => {
      if (!field || typeof field !== 'object') return null;
      const record = field as Record<string, unknown>;
      const id = String(record.id ?? record.key ?? '');
      if (!id) return null;
      const override = overrides[id];
      return override ? { ...record, ...override } : record;
    })
    .filter((field): field is Record<string, unknown> => field != null);

  return fields.length > 0 ? { fields } : undefined;
}

function adaptFormBlockToResolved(
  block: FormBlock,
  catalogEntry?: FormBlockCatalogEntry,
): ResolvedFormBlock {
  const blockType = block.block_type ?? block.type ?? '';
  const config = block.config ?? {};
  const catalog = adaptCatalogFields(catalogEntry, config);

  return {
    id: block.id,
    block_kind: block.block_kind,
    block_type: blockType,
    is_visible: block.is_visible_default !== false,
    is_required: block.is_required ?? block.required ?? false,
    config,
    ...(catalog ? { catalog } : {}),
    ...(config.prefill !== undefined ? { prefill: config.prefill } : {}),
  };
}

/** Staff fallback when GET submission omits resolved_form but form_id is known. */
export async function buildResolvedFormFromTemplate(
  formId: string,
  formVersion?: number,
): Promise<ResolvedForm | null> {
  const [{ blocks }, conditionsResponse, catalogResponse, template] = await Promise.all([
    formsApi.getBlocks(formId),
    formsApi.getConditions(formId).catch(() => ({ conditions: [] as [] })),
    formsApi.getBlockCatalog(),
    formsApi.getTemplate(formId).catch(() => null),
  ]);

  if (!blocks.length) return null;

  const catalogIndex = buildCatalogIndex(flattenBlockCatalog(catalogResponse));
  const version = formVersion ?? template?.version ?? template?.version_number ?? 0;

  return {
    schema_version: '1',
    form_id: formId,
    form_version: version,
    blocks: [...blocks]
      .sort((a, b) => readBlockPosition(a) - readBlockPosition(b))
      .map((block) => adaptFormBlockToResolved(block, lookupCatalogForBlock(catalogIndex, block))),
    conditions: conditionsResponse.conditions ?? [],
  };
}
